import React, { useState, useRef, useEffect } from 'react';
import { AudioPlayerProps } from '../types';
import { generateTTS } from './GameAPI';
import { debugLog } from '../utils/logger';
import { useTTS } from '../context/TTSContext';
import { useTTSCache } from '../context/TTSCacheContext';

const AudioPlayer: React.FC<AudioPlayerProps> = ({ text, type, messageId, autoPlay = false }) => {
    const { ttsEnabled } = useTTS();
    const { getCachedAudio, cacheAudio } = useTTSCache();
    const hasPlayedRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const audioUrlRef = useRef<string | null>(null);

    // Play audio using the text-to-speech API
    const handlePlay = async () => {
        try {
            // Skip if already loading
            if (isLoading) return;

            // Reset any previous errors
            setError(null);
            setIsLoading(true);

            // Check if we have this audio in the cache
            const cachedAudioUrl = getCachedAudio(messageId, text, type);

            if (cachedAudioUrl) {
                // Use cached audio
                debugLog('Using cached audio for message', { messageId });

                // Clean up any previous audio URL
                if (audioUrlRef.current) {
                    URL.revokeObjectURL(audioUrlRef.current);
                }

                // Set the audio source to the cached URL and play
                if (audioRef.current) {
                    audioRef.current.src = cachedAudioUrl;
                    audioRef.current.play();
                    debugLog('Playing cached audio for message', { messageId });
                }

                setIsLoading(false);
                return;
            }

            // Not in cache, generate new audio
            debugLog('Generating audio for message', { messageId, type, textLength: text.length });

            // Generate audio
            const audioBlob = await generateTTS(text, type);

            // Cache the audio blob
            const audioUrl = cacheAudio(messageId, text, type, audioBlob);

            // Update the audio URL ref
            audioUrlRef.current = audioUrl;

            // Set the audio source and play
            if (audioRef.current) {
                audioRef.current.src = audioUrl;
                audioRef.current.play();
                debugLog('Playing newly generated audio for message', { messageId });
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to generate audio';
            debugLog('Audio playback error', { error: errorMessage, messageId });
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Clean up audio URL on unmount
    React.useEffect(() => {
        return () => {
            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
            }
        };
    }, []);

    // Auto-play audio for new messages only
    useEffect(() => {
        // Only play on initial mount (new messages) if autoPlay and TTS are enabled
        if (autoPlay && ttsEnabled && text && !hasPlayedRef.current) {
            debugLog('Auto-playing audio for new message', { messageId, type });
            handlePlay();
            hasPlayedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for TTS enabled/disabled changes, but don't auto-play existing messages
    useEffect(() => {
        // This is intentionally empty to avoid re-playing when ttsEnabled changes
    }, [ttsEnabled]);

    return (
        <div className="audio-player">
            <button
                onClick={handlePlay}
                disabled={isLoading}
                className={`play-button ${isLoading ? 'loading' : ''}`}
                aria-label="Play message audio"
            >
                {isLoading ? '🔊 ...' : '🔊 Play'}
            </button>

            {error && <div className="audio-error">Error: {error}</div>}

            <audio ref={audioRef} />
        </div>
    );
};

export default AudioPlayer;
